"use client";

import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { useTranslations } from "next-intl";
import IconComponent from "@/helpers/functions/getIcon";

export default function OddsCalculator() {
  const [decimal, setDecimal] = useState("");
  const [fractional, setFractional] = useState("");
  const [american, setAmerican] = useState("");
  const [impliedProbability, setImpliedProbability] = useState("");
  const [activeField, setActiveField] = useState(null);

  const t = useTranslations("tools");

  // Reset all fields
  const handleReset = () => {
    setDecimal("");
    setFractional("");
    setAmerican("");
    setImpliedProbability("");
    setActiveField(null);
  };

  // Convert from decimal to all other formats
  const convertFromDecimal = (decimalValue) => {
    if (isNaN(decimalValue) || decimalValue <= 1) return;

    // To fractional
    const fractionalValue = decimalValue - 1;
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const denominator = 1;
    const numerator = fractionalValue;

    // Find the greatest common divisor to simplify the fraction
    const divisor = gcd(
      Math.round(numerator * 100),
      Math.round(denominator * 100),
    );
    const simplifiedNumerator = Math.round(numerator * 100) / divisor;
    const simplifiedDenominator = Math.round(denominator * 100) / divisor;

    // To American
    let americanValue;
    if (decimalValue >= 2) {
      americanValue = (decimalValue - 1) * 100;
    } else {
      americanValue = -100 / (decimalValue - 1);
    }

    // To implied probability
    const impliedProbabilityValue = (1 / decimalValue) * 100;

    if (activeField !== "fractional") {
      setFractional(`${simplifiedNumerator}/${simplifiedDenominator}`);
    }
    if (activeField !== "american") {
      setAmerican(
        americanValue > 0
          ? `+${Math.round(americanValue)}`
          : `${Math.round(americanValue)}`,
      );
    }
    if (activeField !== "impliedProbability") {
      setImpliedProbability(impliedProbabilityValue.toFixed(2));
    }
  };

  // Convert from fractional to all other formats
  const convertFromFractional = (fractionalValue) => {
    if (!fractionalValue.includes("/")) return;

    const [numerator, denominator] = fractionalValue.split("/").map(Number);
    if (isNaN(numerator) || isNaN(denominator) || denominator === 0) return;

    // To decimal
    const decimalValue = numerator / denominator + 1;

    // To American
    let americanValue;
    if (decimalValue >= 2) {
      americanValue = (decimalValue - 1) * 100;
    } else {
      americanValue = -100 / (decimalValue - 1);
    }

    // To implied probability
    const impliedProbabilityValue = (1 / decimalValue) * 100;

    if (activeField !== "decimal") {
      setDecimal(decimalValue.toFixed(2));
    }
    if (activeField !== "american") {
      setAmerican(
        americanValue > 0
          ? `+${Math.round(americanValue)}`
          : `${Math.round(americanValue)}`,
      );
    }
    if (activeField !== "impliedProbability") {
      setImpliedProbability(impliedProbabilityValue.toFixed(2));
    }
  };

  // Convert from American to all other formats
  const convertFromAmerican = (americanValue) => {
    const numericValue = Number.parseFloat(americanValue.replace("+", ""));
    if (isNaN(numericValue) || numericValue === 0) return;

    // To decimal
    let decimalValue;
    if (numericValue > 0) {
      decimalValue = numericValue / 100 + 1;
    } else {
      decimalValue = 100 / -numericValue + 1;
    }

    // To fractional
    const fractionalValue = decimalValue - 1;
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const denominator = 1;
    const numerator = fractionalValue;

    // Find the greatest common divisor to simplify the fraction
    const divisor = gcd(
      Math.round(numerator * 100),
      Math.round(denominator * 100),
    );
    const simplifiedNumerator = Math.round(numerator * 100) / divisor;
    const simplifiedDenominator = Math.round(denominator * 100) / divisor;

    // To implied probability
    const impliedProbabilityValue = (1 / decimalValue) * 100;

    if (activeField !== "decimal") {
      setDecimal(decimalValue.toFixed(2));
    }
    if (activeField !== "fractional") {
      setFractional(`${simplifiedNumerator}/${simplifiedDenominator}`);
    }
    if (activeField !== "impliedProbability") {
      setImpliedProbability(impliedProbabilityValue.toFixed(2));
    }
  };

  // Convert from implied probability to all other formats
  const convertFromImpliedProbability = (probabilityValue) => {
    if (
      isNaN(probabilityValue) ||
      probabilityValue <= 0 ||
      probabilityValue >= 100
    )
      return;

    // To decimal
    const decimalValue = 100 / probabilityValue;

    // To fractional
    const fractionalValue = decimalValue - 1;
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const denominator = 1;
    const numerator = fractionalValue;

    // Find the greatest common divisor to simplify the fraction
    const divisor = gcd(
      Math.round(numerator * 100),
      Math.round(denominator * 100),
    );
    const simplifiedNumerator = Math.round(numerator * 100) / divisor;
    const simplifiedDenominator = Math.round(denominator * 100) / divisor;

    // To American
    let americanValue;
    if (decimalValue >= 2) {
      americanValue = (decimalValue - 1) * 100;
    } else {
      americanValue = -100 / (decimalValue - 1);
    }

    if (activeField !== "decimal") {
      setDecimal(decimalValue.toFixed(2));
    }
    if (activeField !== "fractional") {
      setFractional(`${simplifiedNumerator}/${simplifiedDenominator}`);
    }
    if (activeField !== "american") {
      setAmerican(
        americanValue > 0
          ? `+${Math.round(americanValue)}`
          : `${Math.round(americanValue)}`,
      );
    }
  };

  // Handle decimal input change
  const handleDecimalChange = (e) => {
    const value = e.target.value;
    setDecimal(value);
    setActiveField("decimal");

    const numericValue = Number.parseFloat(value);
    if (!isNaN(numericValue) && numericValue > 1) {
      convertFromDecimal(numericValue);
    }
  };

  // Handle fractional input change
  const handleFractionalChange = (e) => {
    const value = e.target.value;
    setFractional(value);
    setActiveField("fractional");

    if (value.includes("/")) {
      convertFromFractional(value);
    }
  };

  // Handle American input change
  const handleAmericanChange = (e) => {
    const value = e.target.value;
    setAmerican(value);
    setActiveField("american");

    if (value !== "" && value !== "+" && value !== "-") {
      convertFromAmerican(value);
    }
  };

  // Handle implied probability input change
  const handleImpliedProbabilityChange = (e) => {
    const value = e.target.value;
    setImpliedProbability(value);
    setActiveField("impliedProbability");

    const numericValue = Number.parseFloat(value);
    if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100) {
      convertFromImpliedProbability(numericValue);
    }
  };

  // Effect to handle initial conversions when component mounts
  useEffect(() => {
    if (decimal && activeField === "decimal") {
      const numericValue = Number.parseFloat(decimal);
      if (!isNaN(numericValue) && numericValue > 1) {
        convertFromDecimal(numericValue);
      }
    } else if (fractional && activeField === "fractional") {
      convertFromFractional(fractional);
    } else if (american && activeField === "american") {
      convertFromAmerican(american);
    } else if (impliedProbability && activeField === "impliedProbability") {
      const numericValue = Number.parseFloat(impliedProbability);
      if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100) {
        convertFromImpliedProbability(numericValue);
      }
    }
  }, []);

  return (
    <main className="container mx-auto">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="shadow-lg rounded-sm px-4 py-8 lg:px-6 bg-card-foreground">
          <div className="space-y-4">
            <OddsInput
              id="decimal"
              label={t("decimalOdds")}
              type="number"
              icon="calculate"
              min="1.01"
              step="0.01"
              placeholder="2.50"
              value={decimal}
              onChange={handleDecimalChange}
            />
            <OddsInput
              id="fractional"
              icon="splitscreen"
              label={t("fractionalOdds")}
              placeholder="3/2"
              value={fractional}
              onChange={handleFractionalChange}
            />
            <OddsInput
              id="american"
              icon="attach_money"
              label={t("moneylineOdds")}
              placeholder="+150 or -200"
              value={american}
              onChange={handleAmericanChange}
            />
            <OddsInput
              id="impliedProbability"
              icon="percent"
              label={t("impliedProbability")}
              type="number"
              min="0.01"
              max="99.99"
              step="0.01"
              placeholder="40.00"
              value={impliedProbability}
              onChange={handleImpliedProbabilityChange}
            />
          </div>
          <div className="text-base flex items-center leading-none justify-end pt-4">
            <button
              onClick={handleReset}
              className="bg-red text-sm leading-none px-2 gap-1 flex items-center rounded-sm py-1"
            >
              <IconComponent
                iconName="restart_alt"
                className="text-white leading-none  !text-[16px]"
              />
              {t("reset")}
            </button>
          </div>
        </div>
        <div className="space-y-2 flex flex-col justify-center">
          <h2 className="text-xl pt-0 mt-0 mb-0 font-semibold">
            {t("howToConvertOdds")}
          </h2>
          <p className="text-sm text-white/70">
            {t("howToConvertOddsDescription")}
          </p>
          <ul className="text-sm flex flex-col gap-2 pt-4 text-white/80">
            <li className="flex gap-2 items-start p-1 rounded-md">
              <span className="bg-blue-radiant leading-none px-2 rounded-md w-6 text-center py-1 block">
                1
              </span>{" "}
              {t("convertInstructions.step1")}
            </li>
            <li className="flex gap-2 items-start  p-1 rounded-md">
              <span className="bg-pink-radiant leading-none px-2 w-6 text-center py-1 block rounded-md">
                2
              </span>{" "}
              {t("convertInstructions.step2")}
            </li>
            <li className="flex gap-2 items-start p-1 rounded-md">
              <span className="bg-green-radiant leading-none px-2 w-6 text-center py-1 block rounded-md">
                3
              </span>{" "}
              {t("convertInstructions.step3")}
            </li>
          </ul>
          {/* 
          <span className="text-xs block pt-6 text-white/60">
            {t("toolOfferedBy")}
          </span>
          <div className="p-4 rounded-xl bg-card flex justify-between items-center">
            <Image
              src="https://cdn.doutorapostas.pt/7d343a84-28df-4c60-8a0b-8336b770eb68.svg"
              alt="Odds Calculator"
              width={120}
              height={100}
              className="h-auto r"
            />

            <div className="flex gap-2 justify-start items-start">
              <button className="bg-blue hidden lg:flex rounded-sm p-3 grow-0 gap-2 hover:scale-[102%] items-center text-sm font-semibold duration-300 group-hover:bg-blue/80 ">
                {" "}
                <span className="material-symbols-sharp text-white !text-[16px]">
                  open_in_new
                </span>
              </button>
            </div>
          </div>
          */}
        </div>
      </div>
    </main>
  );
}

function OddsInput({
  id,
  label,
  icon = "calculate",
  type = "text",
  min,
  max,
  step,
  value,
  onChange,
  placeholder,
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label
        htmlFor={id}
        className="text-sm font-medium flex gap-1 items-center"
      >
        <span className="material-symbols-sharp text-white !text-[16px]">
          {icon}
        </span>
        {label}
      </Label>
      <input
        id={id}
        type={type}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="px-3 py-3 bg-black/50 text-white/80 rounded-md border text-sm border-white/10"
      />
    </div>
  );
}